import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { RootState } from "..";
import { QuestionResponsesSearchFilter } from "@models/Filter";
import type { RollingAverageConfig } from "@/utils/rolling-average-utils";
import {
  type PointInTimeViewerOptions,
  DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
} from "@models/Dataset";

// Time Period Selection
export type TimePeriod =
  | "all_time"
  | "latest_quarter"
  | "previous_quarter"
  | "latest_semester"
  | "previous_semester"
  | "custom_period";

export interface TimePeriodSelection {
  timePeriod: TimePeriod;
  startDate: string | null;
  endDate: string | null;
}

// Statistical Testing
export type StatTestType = "z-test" | "t-test";
export type SignificanceLevel = "90%" | "95%" | "99%";

export interface StatisticalTestingConfig {
  enabled: boolean;
  testType: StatTestType;
  significanceLevel: SignificanceLevel;
  minimumSampleSize: number;
  compareToTotal: boolean;
}

export interface TableConfig {
  // Chart basic settings
  title: string;
  subtitle: string;
  titleBold: boolean;
  titleUnderline: boolean;

  // Question metadata
  questionLabel?: string;
  questionName?: string;

  // Data formatting
  percentageFormatting: number;
  weight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT";

  // Data sorting
  dataSorting: "ascending" | "descending" | "none" | "manual";

  // Table header labels
  rowHeaderLabel: string;
  columnHeaderLabel: string;

  // Row visibility - stores variableIds of hidden rows
  hiddenRows: string[];

  // Row order - stores variableIds in display order (for drag-and-drop reordering)
  rowOrder: string[];

  // Custom row labels - stores variableId -> custom label mapping
  customRowLabels: Record<string, string>;

  // Time Period Selection
  timePeriodSelection: TimePeriodSelection;

  // Rolling Average (PRD-26)
  rollingAverage: RollingAverageConfig;
  effectiveRollingPeriodCount?: number;
  rollingPeriodsWarningMessage?: string;
  comparisonRollingLabel?: string;
  missingRollingPeriodLabels?: string[];

  // Statistical Testing
  statisticalTesting: StatisticalTestingConfig;

  // Viewer Time Options 
  viewerOptions: PointInTimeViewerOptions;

  // Date range from API response (for statistical testing)
  responsesMinDate?: string;
  responsesMaxDate?: string;

  // Table data
  data: Array<{
    variableId: string;
    variableLabel: string;
    surveys: Array<{
      percentage: number;
      weightedCount: number;
      totalCount: number;
      rank: number | null;
      descriptiveMetrics?: Array<{
        valueMetric: string;
        value: number | null;
        baseN: number;
        suppressed: boolean;
        statSignificance?: number;
      }>;
    }>;
  }>;

  // Original data (preserved for reset functionality)
  originalData: Array<{
    variableId: string;
    variableLabel: string;
    surveys: Array<{
      percentage: number;
      weightedCount: number;
      totalCount: number;
      rank: number | null;
      descriptiveMetrics?: Array<{
        valueMetric: string;
        value: number | null;
        baseN: number;
        suppressed: boolean;
        statSignificance?: number;
      }>;
    }>;
  }>;
}

interface Questions extends Record<string, any> {
  id: string;
  label: string;
  name: string;
  responseCount?: number;
  responses?: number;
}

export interface TableConfigState {
  currentConfig: TableConfig;
  savedConfigs: TableConfig[];
  isDrawerOpen: boolean;
  previewMode: boolean;
  question: Questions | null;
  editingWidgetId: string | null;
  datasetId: string | null;
  datasetName: string | null;
  questionId: string | null;
  datasetVersionId: number | null;
  activeFilters: QuestionResponsesSearchFilter[];
  // Toggle to apply/ignore filters in preview
  applyFilters: boolean;
}

const defaultTableConfig: TableConfig = {
  title: "Data Table",
  subtitle: "",
  titleBold: false,
  titleUnderline: false,
  percentageFormatting: 0, // Default to 0 decimals per PRD
  weight: "DISABLED",
  dataSorting: "none",
  rowHeaderLabel: "RQ DIMENSIONS",
  columnHeaderLabel: "MEAN",
  hiddenRows: [],
  rowOrder: [],
  customRowLabels: {},
  timePeriodSelection: {
    timePeriod: "all_time",
    startDate: null,
    endDate: null,
  },
  rollingAverage: {
    enabled: false,
    windowSize: 2,
  },
  statisticalTesting: {
    enabled: false,
    testType: "t-test",
    significanceLevel: "90%",
    minimumSampleSize: 30,
    compareToTotal: false,
  },
  viewerOptions: DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
  data: [],
  originalData: [],
};

export const initialState: TableConfigState = {
  currentConfig: { ...defaultTableConfig },
  savedConfigs: [],
  isDrawerOpen: false,
  previewMode: true,
  question: null,
  editingWidgetId: null,
  datasetId: null,
  datasetName: null,
  questionId: null,
  datasetVersionId: null,
  activeFilters: [],
  applyFilters: false,
};

export const tableConfigSlice = createSlice({
  name: "tableConfig",
  initialState,
  reducers: {
    openTableDrawer: (state, action: PayloadAction<string>) => {
      state.isDrawerOpen = true;
      state.editingWidgetId = action.payload;
    },
    closeTableDrawer: (state) => {
      state.isDrawerOpen = false;
      state.editingWidgetId = null;
      // Reset active filters when closing drawer
      state.activeFilters = [];
      state.applyFilters = false;
    },
    togglePreviewMode: (state) => {
      state.previewMode = !state.previewMode;
    },
    updateChartTitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.title = action.payload;
    },
    updateChartSubtitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.subtitle = action.payload;
    },
    updateTitleBold: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleBold = action.payload;
    },
    updateTitleUnderline: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleUnderline = action.payload;
    },
    updatePercentageFormatting: (state, action: PayloadAction<number>) => {
      state.currentConfig.percentageFormatting = action.payload;
    },
    updateWeight: (
      state,
      action: PayloadAction<"DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT">,
    ) => {
      state.currentConfig.weight = action.payload;
    },
    updateDataSorting: (
      state,
      action: PayloadAction<"ascending" | "descending" | "none" | "manual">,
    ) => {
      state.currentConfig.dataSorting = action.payload;
    },
    updateRowHeaderLabel: (state, action: PayloadAction<string>) => {
      state.currentConfig.rowHeaderLabel = action.payload;
    },
    updateColumnHeaderLabel: (state, action: PayloadAction<string>) => {
      state.currentConfig.columnHeaderLabel = action.payload;
    },
    toggleRowVisibility: (state, action: PayloadAction<string>) => {
      const variableId = action.payload;
      const index = state.currentConfig.hiddenRows.indexOf(variableId);

      if (index > -1) {
        // Row is hidden, show it
        state.currentConfig.hiddenRows.splice(index, 1);
      } else {
        // Row is visible, hide it
        state.currentConfig.hiddenRows.push(variableId);
      }
    },
    showAllRows: (state) => {
      state.currentConfig.hiddenRows = [];
    },
    hideAllRows: (state) => {
      state.currentConfig.hiddenRows = state.currentConfig.data.map(
        (item) => item.variableId,
      );
    },
    reorderRows: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      const { fromIndex, toIndex } = action.payload;
      const rowOrder = [...state.currentConfig.rowOrder];
      const [removed] = rowOrder.splice(fromIndex, 1);
      rowOrder.splice(toIndex, 0, removed);
      state.currentConfig.rowOrder = rowOrder;
    },
    resetRowOrder: (state) => {
      // Reset row order to match original data order
      state.currentConfig.rowOrder = state.currentConfig.data.map(
        (item) => item.variableId,
      );
    },
    updateCustomRowLabel: (
      state,
      action: PayloadAction<{ id: string; label: string }>,
    ) => {
      state.currentConfig.customRowLabels[action.payload.id] =
        action.payload.label;
    },
    resetCustomRowLabel: (state, action: PayloadAction<string>) => {
      delete state.currentConfig.customRowLabels[action.payload];
    },
    updateQuestions: (state, action: PayloadAction<Questions | null>) => {
      state.question = action.payload;
      if (action.payload?.name) {
        state.currentConfig.title = action.payload.name;
      }
      // Store question metadata in config for persistence
      if (action.payload) {
        state.currentConfig.questionLabel = action.payload.label;
        state.currentConfig.questionName = action.payload.name;
      }
    },
    updateChartMetadata: (
      state,
      action: PayloadAction<{
        datasetId: string;
        questionId: string;
        datasetVersionId?: number | null;
        datasetName?: string;
        activeFilters?: QuestionResponsesSearchFilter[];
      }>,
    ) => {
      state.datasetId = action.payload.datasetId;
      state.questionId = action.payload.questionId;
      state.datasetVersionId = action.payload.datasetVersionId ?? null;
      state.datasetName = action.payload.datasetName ?? null;
      // Set active filters if provided (including empty array)
      if (action.payload.activeFilters !== undefined) {
        state.activeFilters = action.payload.activeFilters;
      }
    },
    // Time Period Selection actions
    updateTimePeriod: (state, action: PayloadAction<TimePeriod>) => {
      state.currentConfig.timePeriodSelection.timePeriod = action.payload;
      // Reset dates when changing time period
      if (action.payload !== "custom_period") {
        state.currentConfig.timePeriodSelection.startDate = null;
        state.currentConfig.timePeriodSelection.endDate = null;
      }
    },
    updateTimePeriodDates: (
      state,
      action: PayloadAction<{
        startDate: string | null;
        endDate: string | null;
      }>,
    ) => {
      state.currentConfig.timePeriodSelection.startDate =
        action.payload.startDate;
      state.currentConfig.timePeriodSelection.endDate = action.payload.endDate;
    },
    // Rolling Average actions
    updateRollingAverageEnabled: (state, action: PayloadAction<boolean>) => {
      if (!state.currentConfig.rollingAverage) {
        state.currentConfig.rollingAverage = { enabled: false, windowSize: 2 };
      }
      state.currentConfig.rollingAverage.enabled = action.payload;
      if (!action.payload) {
        state.currentConfig.rollingAverage.windowSize = 2;
      }
    },
    updateRollingAverageWindowSize: (state, action: PayloadAction<number>) => {
      if (!state.currentConfig.rollingAverage) {
        state.currentConfig.rollingAverage = { enabled: false, windowSize: 2 };
      }
      state.currentConfig.rollingAverage.windowSize = action.payload;
    },

    // Viewer Options actions
    updateViewerOptions: (
      state,
      action: PayloadAction<PointInTimeViewerOptions>,
    ) => {
      state.currentConfig.viewerOptions = action.payload;
    },

    // Statistical Testing actions
    updateStatTestingEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.statisticalTesting.enabled = action.payload;
      // Disable compare to total if stat testing is disabled
      if (!action.payload) {
        state.currentConfig.statisticalTesting.compareToTotal = false;
      }
    },
    updateStatTestType: (state, action: PayloadAction<StatTestType>) => {
      state.currentConfig.statisticalTesting.testType = action.payload;
    },
    updateSignificanceLevel: (
      state,
      action: PayloadAction<SignificanceLevel>,
    ) => {
      state.currentConfig.statisticalTesting.significanceLevel = action.payload;
    },
    updateMinimumSampleSize: (state, action: PayloadAction<number>) => {
      state.currentConfig.statisticalTesting.minimumSampleSize = action.payload;
    },
    updateCompareToTotal: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.statisticalTesting.compareToTotal = action.payload;
    },
    // Filter toggle action
    toggleApplyFilters: (state) => {
      state.applyFilters = !state.applyFilters;
    },
    updateTableData: (
      state,
      action: PayloadAction<{
        data: TableConfig["data"];
        responsesMinDate?: string;
        responsesMaxDate?: string;
        effectiveRollingPeriodCount?: number;
        rollingPeriodsWarningMessage?: string;
        comparisonRollingLabel?: string;
        missingRollingPeriodLabels?: string[];
      }>,
    ) => {
      const data = action.payload.data.map((item) => ({
        ...item,
        surveys: item.surveys.map((survey) => ({ ...survey })),
      }));

      state.currentConfig.data = data;
      state.currentConfig.originalData = data.map((item) => ({
        ...item,
        surveys: item.surveys.map((survey) => ({ ...survey })),
      }));

      // Initialize rowOrder if not already set or if data changed
      if (
        state.currentConfig.rowOrder.length === 0 ||
        state.currentConfig.rowOrder.length !== data.length
      ) {
        state.currentConfig.rowOrder = data.map((item) => item.variableId);
      }

      // Store responsesMinDate and responsesMaxDate if provided
      if (action.payload.responsesMinDate !== undefined) {
        state.currentConfig.responsesMinDate = action.payload.responsesMinDate;
      }
      if (action.payload.responsesMaxDate !== undefined) {
        state.currentConfig.responsesMaxDate = action.payload.responsesMaxDate;
      }

      // Update rolling average response fields
      state.currentConfig.effectiveRollingPeriodCount =
        action.payload.effectiveRollingPeriodCount;
      state.currentConfig.rollingPeriodsWarningMessage =
        action.payload.rollingPeriodsWarningMessage;
      state.currentConfig.comparisonRollingLabel =
        action.payload.comparisonRollingLabel;
      state.currentConfig.missingRollingPeriodLabels =
        action.payload.missingRollingPeriodLabels;
    },
    saveCurrentConfig: (state) => {
      const configToSave = { ...state.currentConfig };
      const tp = configToSave.timePeriodSelection?.timePeriod;
      if (tp && tp !== "all_time" && tp !== "custom_period") {
        configToSave.timePeriodSelection = {
          ...configToSave.timePeriodSelection,
          startDate: null,
          endDate: null,
        };
      }
      state.savedConfigs.push(configToSave);
    },
    loadConfig: (state, action: PayloadAction<TableConfig>) => {
      const hasCurrentOriginalData =
        state.currentConfig.originalData.length > 0;
      const hasLoadedOriginalData =
        action.payload.originalData && action.payload.originalData.length > 0;

      // Merge defaults for fields that may be absent in older saved configs
      const payload: TableConfig = {
        ...action.payload,
        rollingAverage: action.payload.rollingAverage ?? {
          enabled: false,
          windowSize: 2,
        },
        statisticalTesting: action.payload.statisticalTesting ?? {
          enabled: false,
        },
        timePeriodSelection: action.payload.timePeriodSelection ?? {
          timePeriod: "all_time",
          startDate: null,
          endDate: null,
        },
        viewerOptions:
          action.payload.viewerOptions ?? DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
      };

      Object.assign(state.currentConfig, payload);

      if (!hasLoadedOriginalData && hasCurrentOriginalData) {
        state.currentConfig.originalData = state.currentConfig.originalData;
      }
    },
    resetToDefault: (state) => {
      const originalData =
        state.currentConfig.originalData.length > 0
          ? state.currentConfig.originalData.map((item) => ({
              ...item,
              surveys: item.surveys.map((survey) => ({ ...survey })),
            }))
          : [];

      Object.assign(state.currentConfig, {
        ...defaultTableConfig,
        originalData,
        data: originalData,
      });

      // Reset filters when resetting config
      state.activeFilters = [];
      state.applyFilters = false;
    },
  },
});

export const {
  openTableDrawer,
  closeTableDrawer,
  togglePreviewMode,
  updateChartTitle,
  updateChartSubtitle,
  updateTitleBold,
  updateTitleUnderline,
  updatePercentageFormatting,
  updateWeight,
  updateDataSorting,
  updateRowHeaderLabel,
  updateColumnHeaderLabel,
  toggleRowVisibility,
  showAllRows,
  hideAllRows,
  reorderRows,
  resetRowOrder,
  updateCustomRowLabel,
  resetCustomRowLabel,
  updateTimePeriod,
  updateTimePeriodDates,
  updateRollingAverageEnabled,
  updateRollingAverageWindowSize,
  updateViewerOptions,
  updateStatTestingEnabled,
  updateStatTestType,
  updateSignificanceLevel,
  updateMinimumSampleSize,
  updateCompareToTotal,
  toggleApplyFilters,
  updateTableData,
  updateQuestions,
  updateChartMetadata,
  saveCurrentConfig,
  loadConfig,
  resetToDefault,
} = tableConfigSlice.actions;

export const tableConfigDispatcher = createDispatchers(
  tableConfigSlice.actions,
);
export const tableConfigSelector = (state: RootState) => state.tableConfig;

export const tableConfigReducer = tableConfigSlice.reducer;
